import { useCallback, useEffect, useState } from 'react';
import {
  findMasterLender,
  LenderService,
  type LenderParentRecord,
} from '@/shared/api/services/lenderService';

let cachedMasterLenders: LenderParentRecord[] | null = null;
let pendingMasterLoad: Promise<LenderParentRecord[]> | null = null;

function loadMasterLenders(): Promise<LenderParentRecord[]> {
  if (cachedMasterLenders) {
    return Promise.resolve(cachedMasterLenders);
  }
  if (!pendingMasterLoad) {
    pendingMasterLoad = LenderService.fetchLenders({ skipErrorCapture: true })
      .then((response) => {
        if (response.success && Array.isArray(response.data)) {
          cachedMasterLenders = response.data;
          return response.data;
        }
        return [] as LenderParentRecord[];
      })
      .catch(() => [] as LenderParentRecord[])
      .finally(() => {
        pendingMasterLoad = null;
      });
  }
  return pendingMasterLoad;
}

function descriptionFor(
  name: string,
  lenders: LenderParentRecord[] | null | undefined,
): string {
  const match = findMasterLender(lenders, name);
  return match?.description?.trim() ?? '';
}

// Visible label only. The stored creditor remains the short lender name.
export function resolveLenderDisplayName(
  name: string | null | undefined,
  lenders: LenderParentRecord[] | null | undefined,
): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) {
    return '';
  }
  return descriptionFor(trimmed, lenders) || trimmed;
}

export function useLenderDisplayName(
  extraLenders?: LenderParentRecord[] | null,
): (name: string | null | undefined) => string {
  const [masterLenders, setMasterLenders] = useState<LenderParentRecord[]>(
    cachedMasterLenders ?? [],
  );

  useEffect(() => {
    let cancelled = false;
    void loadMasterLenders().then((rows) => {
      if (!cancelled) {
        setMasterLenders(rows);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return useCallback(
    (name) => {
      const trimmed = (name ?? '').trim();
      if (!trimmed) {
        return '';
      }
      // Prefer a description from the full lender master. The debt-list payload
      // often has the short lender name and no description.
      return (
        descriptionFor(trimmed, masterLenders) ||
        descriptionFor(trimmed, extraLenders) ||
        trimmed
      );
    },
    [extraLenders, masterLenders],
  );
}
